import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { lastValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { OrganisationSettingsService, OrganisationSettingWithGplView } from '@src/api';
import { AlertService, DocumentService, PreferencesService } from '@src/core/services';
import { AuthUserService, SessionService } from '@src/app/modules/auth';
import { BaseResizableComponent } from '@src/app/modules/base';
import { APP_CONFIG } from '@src/core';
import { InternalMessengerService } from '@src/app/modules/internal-messenger';

import { FormData, FormDataControls } from './types';
import { PUBLIC_CHAT_NAME } from './constants';

@Component({
  selector: 'app-preferences',
  templateUrl: './preferences.component.html',
  styleUrls: ['./preferences.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreferencesComponent extends BaseResizableComponent {
  private readonly session = inject(SessionService);
  private readonly messengerService = inject(InternalMessengerService);
  private readonly api = inject(OrganisationSettingsService);
  private readonly alertService = inject(AlertService);
  private readonly preferencesService = inject(PreferencesService);
  private readonly authUserService = inject(AuthUserService);
  private readonly translateService = inject(TranslateService);
  private readonly documentService = inject(DocumentService);
  private readonly ngxPermissionsService = inject(NgxPermissionsService);

  form = new FormGroup<FormDataControls>({
    showParentOrgUsersInLists: new FormControl(false, { nonNullable: true }),
    showAllUsersPhonesAndEmails: new FormControl(false, { nonNullable: true }),
    showOnlyChannelsInNewsFeed: new FormControl(false, { nonNullable: true }),
    darkThemeByDefault: new FormControl(false, { nonNullable: true }),
    allowUserRegistrationForm: new FormControl(false, { nonNullable: true }),
    showOnlyLoyaltyProgram: new FormControl(false, { nonNullable: true }),
    templateForNewcomerOld: new FormControl(null),
    templateForNewcomerNew: new FormControl(null),
  });

  settings?: OrganisationSettingWithGplView;

  loading = false;

  saving = false;

  readonly maxDocFileSize = APP_CONFIG.fileSizeMax.doc;

  get userRegistrationLink() {
    const associationId = this.authUserService.authUser$.value?.parentOrganisationId;

    // HACK для ios
    const protocol = window.location.protocol === 'app:' ? 'https:' : window.location.protocol;

    return associationId ? protocol + '//' + window.location.host + '/registration/' + associationId : undefined;
  }

  override async ngOnInit(): Promise<void> {
    super.ngOnInit();

    this.form.controls.templateForNewcomerOld.valueChanges.pipe(takeUntil(this.destroyed$$)).subscribe(() => {
      this.form.markAsDirty();
    });

    this.form.controls.templateForNewcomerNew.valueChanges.pipe(takeUntil(this.destroyed$$)).subscribe(() => {
      this.form.markAsDirty();
    });

    this.form.valueChanges.pipe(takeUntil(this.destroyed$$)).subscribe(() => {
      this.save();
    });

    await this.getData();
  }

  async save(): Promise<void> {
    if (!this.form.dirty || this.form.disabled || this.saving) {
      return;
    }

    this.saving = true;
    this.form.disable();

    const formData = this.form.value as FormData;

    const permissions = this.ngxPermissionsService.getPermissions();
    if (!('preferenceShowOnlyLoyaltyProgramEditing' in permissions)) {
      delete formData.showOnlyLoyaltyProgram;
    }

    const newDocument = formData.templateForNewcomerNew
      ? await lastValueFrom(this.documentService.addDocuments([formData.templateForNewcomerNew]))
      : null;
    const templateForNewcomerId = newDocument?.files?.[0].id
      ? newDocument.files[0].id
      : formData.templateForNewcomerOld?.id;

    const observable = this.api.editOrganisationSettings({
      ...this.settings,
      ...formData,
      organisationId: this.settings?.organisationId ?? this.authUserService.user?.organisationId,
      templateForNewcomerId,
    });

    observable.pipe(takeUntil(this.destroyed$$)).subscribe({
      next: async () => {
        this.alertService.success(this.translateService.instant('components.preferences.alerts.successes.saveData'));

        await this.getData();

        this.form.enable();
        this.saving = false;
      },
      error: err => {
        this.alertService.error(err);

        this.form.enable();
        this.saving = false;
      },
    });
  }

  private async getData() {
    try {
      this.loading = true;

      this.settings = await this.preferencesService.refresh();
      const templateForNewcomerOld = this.settings.templateForNewcomerId
        ? {
            id: this.settings.templateForNewcomerId,
            name: this.translateService.instant('components.preferences.constants.templateForNewcomerFileName'),
          }
        : null;

      const value = {
        showParentOrgUsersInLists: this.settings.showParentOrgUsersInLists,
        showAllUsersPhonesAndEmails: this.settings.showAllUsersPhonesAndEmails,
        showOnlyChannelsInNewsFeed: this.settings.showOnlyChannelsInNewsFeed,
        darkThemeByDefault: this.settings.darkThemeByDefault,
        allowUserRegistrationForm: this.settings.allowUserRegistrationForm,
        showOnlyLoyaltyProgram: this.settings.showOnlyLoyaltyProgram,
        templateForNewcomerOld,
      };
      this.form.reset(value);
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  get messengerSettingsVisible() {
    return !!this.session.appConfig?.internalMessengerUrl && !this.settings?.publicChatId;
  }

  createPublicChat() {
    this.messengerService.createPublicChat(PUBLIC_CHAT_NAME).subscribe(async () => {
      this.alertService.success(
        this.translateService.instant('components.preferences.alerts.successes.publicChatCreated'),
      );

      await this.getData();
    });
  }
}
